/**
 * Viewport — zoom/pan state machine for the canvas editor.
 *
 * Scale ranges from MIN_SCALE (10%) to MAX_SCALE (400%) in STEP (10%) increments.
 * Offsets represent the canvas-space translation in CSS pixels.
 */

export const MIN_SCALE = 0.1;
export const MAX_SCALE = 4.0;
export const STEP = 0.1;

/** Mutable viewport state. */
const viewport = {
  scale: 1.0,
  offsetX: 0,
  offsetY: 0,
};

/**
 * Apply the current viewport transform to a canvas context.
 * Call this at the start of each draw loop (after clearing).
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} dpr - devicePixelRatio, default 1
 */
export function applyTransform(ctx, dpr = 1) {
  ctx.setTransform(
    dpr * viewport.scale,
    0,
    0,
    dpr * viewport.scale,
    viewport.offsetX * dpr,
    viewport.offsetY * dpr
  );
}

/**
 * Reset the canvas transform to the identity matrix.
 *
 * @param {CanvasRenderingContext2D} ctx
 */
export function resetTransform(ctx) {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
}

/**
 * Zoom anchored to a point in CSS-pixel canvas space (e.g. cursor position).
 *
 * The point under the cursor stays fixed after the zoom.
 *
 * @param {number} cursorX - CSS pixel x relative to canvas
 * @param {number} cursorY - CSS pixel y relative to canvas
 * @param {number} delta   - positive = zoom in, negative = zoom out (use STEP = 0.1)
 */
export function zoomAtPoint(cursorX, cursorY, delta) {
  const oldScale = viewport.scale;
  // Round to nearest STEP to avoid floating-point drift
  const newScale = Math.round(
    Math.min(MAX_SCALE, Math.max(MIN_SCALE, oldScale + delta)) / STEP
  ) * STEP;

  if (newScale === oldScale) return;

  // Adjust offsets so the canvas-space point under the cursor stays fixed
  viewport.offsetX = cursorX - (cursorX - viewport.offsetX) * (newScale / oldScale);
  viewport.offsetY = cursorY - (cursorY - viewport.offsetY) * (newScale / oldScale);
  viewport.scale = newScale;
}

/**
 * Set zoom level anchored to the canvas centre.
 *
 * @param {number} newScale   - target scale (clamped to [MIN_SCALE, MAX_SCALE])
 * @param {number} canvasWidth  - logical (CSS pixel) canvas width
 * @param {number} canvasHeight - logical (CSS pixel) canvas height
 */
export function setZoom(newScale, canvasWidth, canvasHeight) {
  const clamped =
    Math.round(
      Math.min(MAX_SCALE, Math.max(MIN_SCALE, newScale)) / STEP
    ) * STEP;
  zoomAtPoint(canvasWidth / 2, canvasHeight / 2, clamped - viewport.scale);
}

/**
 * Reset to 100% zoom with no offset — show the entire bounding box.
 */
export function fitToView() {
  viewport.scale = 1.0;
  viewport.offsetX = 0;
  viewport.offsetY = 0;
}

/**
 * Return a read-only copy of the current viewport state.
 *
 * @returns {{ scale: number, offsetX: number, offsetY: number }}
 */
export function getViewport() {
  return { scale: viewport.scale, offsetX: viewport.offsetX, offsetY: viewport.offsetY };
}

/**
 * Translate the viewport by (dx, dy) CSS pixels.
 *
 * @param {number} dx
 * @param {number} dy
 */
export function pan(dx, dy) {
  viewport.offsetX += dx;
  viewport.offsetY += dy;
}
